// src/components/modules/TAI/PlanillaTAICard.js

import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Row,
  Col,
  Tag,
  Typography,
  Space,
  Checkbox,
  Tooltip,
  Button,
} from "antd";
import {
  UserOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  PhoneOutlined,
  IdcardOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import dayjs from "../../../utils/dayjs";

const { Text } = Typography;

// Colores por estado de planilla (solo 3 estados)
const ESTADO_COLORS = {
  generada: { color: "#8c8c8c", bg: "#fafafa", label: "Generada" },
  recibida: { color: "#52c41a", bg: "#f6ffed", label: "Recibida" },
  facturada: { color: "#722ed1", bg: "#f9f0ff", label: "Facturada" },
};

// Colores por tipo de trayecto
const TRAYECTO_COLORS = {
  interveredal: "#722ed1",
  intermunicipal: "#2f54eb",
  urbano: "#13c2c2",
};

const PlanillaTAICard = ({
  planilla,
  isSelected,
  onSelect,
  onCambiarEstado,
  hasPermission,
}) => {
  const estadoConfig = ESTADO_COLORS[planilla.estado] || ESTADO_COLORS.generada;
  const trayectoColor = TRAYECTO_COLORS[planilla.tipo_trayecto] || "#8c8c8c";

  // Puede cambiar estado si tiene permiso y no está en estado final
  const canCambiarEstado =
    hasPermission("CAMBIAR_ESTADO_PLANILLA") &&
    planilla.estado !== "facturada";

  return (
    <Card
      className={`planilla-tai-card ${isSelected ? "selected" : ""}`}
      style={{
        borderLeft: `4px solid ${estadoConfig.color}`,
        marginBottom: 8,
      }}
      bodyStyle={{ padding: "12px 16px" }}
    >
      <Row gutter={[16, 8]} align="middle">
        {/* Checkbox y Código */}
        <Col xs={24} sm={6} md={3}>
          <Space>
            <Checkbox
              checked={isSelected}
              onChange={(e) => onSelect(e.target.checked)}
            />
            <Tooltip title={`ID: ${planilla.id}`}>
              <Tag color="#722ed1" style={{ fontWeight: "bold" }}>
                {planilla.codigo_planilla}
              </Tag>
            </Tooltip>
          </Space>
        </Col>

        {/* Fecha y Hora */}
        <Col xs={12} sm={6} md={2}>
          <Space direction="vertical" size={0}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              <CalendarOutlined /> Servicio
            </Text>
            <Text strong>{dayjs(planilla.fecha_servicio).format("DD/MM/YY")}</Text>
            {planilla.hora_servicio && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                <ClockCircleOutlined /> {planilla.hora_servicio?.slice(0, 5)}
              </Text>
            )}
          </Space>
        </Col>

        {/* Paciente */}
        <Col xs={24} sm={12} md={4}>
          <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: 13 }}>
              <UserOutlined /> {planilla.paciente?.nombre || "Sin paciente"}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              <IdcardOutlined />{" "}
              {planilla.paciente?.tipoDocumento?.descripcion || planilla.paciente?.tipo_documento_id} {planilla.paciente?.numero_documento}
            </Text>
            {planilla.paciente?.telefono && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                <PhoneOutlined /> {planilla.paciente?.telefono}
              </Text>
            )}
          </Space>
        </Col>

        {/* Origen y Destino */}
        <Col xs={24} sm={12} md={4}>
          <Space direction="vertical" size={0} style={{ width: "100%" }}>
            <Text style={{ fontSize: 12 }}>
              <EnvironmentOutlined style={{ color: "#52c41a" }} />{" "}
              <Text strong>{planilla.origen || "-"}</Text>
            </Text>
            <Text style={{ fontSize: 12 }}>
              <EnvironmentOutlined style={{ color: "#f5222d" }} />{" "}
              <Text strong>{planilla.destino || "-"}</Text>
            </Text>
          </Space>
        </Col>

        {/* Tipo de Traslado y Trayecto */}
        <Col xs={12} sm={4} md={2}>
          <Space direction="vertical" size={0}>
            {planilla.tipo_trayecto && (
              <Tag color={trayectoColor} style={{ margin: 0 }}>
                {planilla.tipo_trayecto.charAt(0).toUpperCase() +
                  planilla.tipo_trayecto.slice(1)}
              </Tag>
            )}
            {planilla.trayecto && (
              <Text type="secondary" style={{ fontSize: 10 }}>
                {planilla.trayecto}
              </Text>
            )}
          </Space>
        </Col>

        {/* Transportadora */}
        <Col xs={12} sm={6} md={3}>
          {planilla.transportadora ? (
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                <TeamOutlined /> Transportadora
              </Text>
              <Text strong style={{ fontSize: 12, color: "#1890ff" }}>
                {planilla.transportadora.nombre}
              </Text>
            </Space>
          ) : (
            <Text type="secondary" style={{ fontSize: 11 }}>
              <TeamOutlined /> Sin transportadora
            </Text>
          )}
        </Col>

        {/* Número de Tiquete */}
        <Col xs={12} sm={4} md={2}>
          {planilla.numero_tiquete ? (
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 10 }}>
                Tiquete
              </Text>
              <Text code style={{ fontSize: 11 }}>
                {planilla.numero_tiquete}
              </Text>
            </Space>
          ) : (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Sin tiquete
            </Text>
          )}
        </Col>

        {/* Valor */}
        <Col xs={12} sm={4} md={2}>
          {planilla.valor_tiquete ? (
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 10 }}>
                <DollarOutlined /> Valor
              </Text>
              <Text strong style={{ fontSize: 12, color: "#52c41a" }}>
                ${parseFloat(planilla.valor_tiquete).toLocaleString("es-CO")}
              </Text>
            </Space>
          ) : (
            <Text type="secondary" style={{ fontSize: 11 }}>
              -
            </Text>
          )}
        </Col>

        {/* Estado */}
        <Col xs={12} sm={4} md={2}>
          <Tag
            color={estadoConfig.color}
            style={{
              backgroundColor: estadoConfig.bg,
              color: estadoConfig.color,
              fontWeight: "bold",
              border: `1px solid ${estadoConfig.color}`,
            }}
          >
            {estadoConfig.label.toUpperCase()}
          </Tag>
        </Col>

        {/* Acciones */}
        <Col xs={12} sm={4} md={2}>
          {canCambiarEstado && (
            <Tooltip title="Cambiar estado">
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                size="small"
                onClick={onCambiarEstado}
                style={{ color: "#722ed1" }}
              />
            </Tooltip>
          )}
        </Col>
      </Row>

      {/* Fechas de seguimiento */}
      <div
        style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: "1px dashed #e8e8e8",
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        {planilla.fecha_recepcion && (
          <Text type="secondary" style={{ fontSize: 10 }}>
            <CheckCircleOutlined /> Recibida: {dayjs(planilla.fecha_recepcion).format("DD/MM/YY HH:mm")}
          </Text>
        )}
        {planilla.fecha_facturacion && (
          <Text type="secondary" style={{ fontSize: 10 }}>
            <FileTextOutlined /> Facturada: {dayjs(planilla.fecha_facturacion).format("DD/MM/YY HH:mm")}
          </Text>
        )}
        {planilla.observaciones && (
          <Text type="secondary" style={{ fontSize: 10, maxWidth: 300 }} ellipsis>
            Obs: {planilla.observaciones}
          </Text>
        )}
      </div>
    </Card>
  );
};

PlanillaTAICard.propTypes = {
  planilla: PropTypes.shape({
    id: PropTypes.number.isRequired,
    codigo_planilla: PropTypes.string.isRequired,
    fecha_servicio: PropTypes.string.isRequired,
    hora_servicio: PropTypes.string,
    origen: PropTypes.string,
    destino: PropTypes.string,
    tipo_trayecto: PropTypes.string,
    trayecto: PropTypes.string,
    numero_tiquete: PropTypes.string,
    valor_tiquete: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    estado: PropTypes.string.isRequired,
    fecha_envio: PropTypes.string,
    fecha_canje: PropTypes.string,
    fecha_uso: PropTypes.string,
    fecha_recepcion: PropTypes.string,
    fecha_facturacion: PropTypes.string,
    nombre_firma_paciente: PropTypes.string,
    documento_firma_paciente: PropTypes.string,
    firma_paciente: PropTypes.string,
    observaciones: PropTypes.string,
    paciente: PropTypes.shape({
      id: PropTypes.number,
      nombre: PropTypes.string,
      tipo_documento_id: PropTypes.number,
      numero_documento: PropTypes.string,
      telefono: PropTypes.string,
    }),
    transportadora: PropTypes.shape({
      id: PropTypes.number,
      nombre: PropTypes.string,
    }),
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onCambiarEstado: PropTypes.func.isRequired,
  hasPermission: PropTypes.func.isRequired,
};

export default PlanillaTAICard;
